import React, { useEffect, useMemo, useState } from 'react';
import { researchosService } from '../../services/researchosService';

// The Research OVERVIEW tab for YouTube Chatter — the ingest→knowledge pipeline at a glance, the
// latest takeaways (top 2, expandable), and the most-discussed names. Self-fetching (polls while a
// bulk run populates the graph). The deterministic Narrative Tracker (scoring: attention /
// favorability / momentum + chatter volume) now lives in its own sibling "Narrative" tab; the admin
// ingest TOOLS live in the "Pipeline" tab band. Visible to every Research OS user.
const GOLD = 'rgb(var(--brand-gold-rgb))';
const STANCE = { bullish: '#16a34a', bearish: '#dc2626', neutral: '#9ca3af' };
const nfmt = (v) => (v == null ? '—' : Number(v).toLocaleString());
const pct = (num, den) => (den ? Math.round((num / den) * 100) : 0);

// ── a thin labeled progress bar (gold fill), used for coverage ratios ──
function Meter({ value, tone = GOLD }) {
  return (
    <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-200/70 dark:bg-gray-800">
      <div className="h-full rounded-full transition-all"
        style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: tone }} />
    </div>
  );
}

// One cell of the segmented KPI band. Cells carry an explicit background so the 1px grid lines
// (rendered by the parent's gap-px over a tinted container) read cleanly; `tint` shades the
// knowledge-graph row a touch darker than the ingest-funnel row.
function Cell({ label, value, sub, meter, tone, lead, tint }) {
  return (
    <div className={`flex min-h-[104px] flex-col justify-between p-4 ${tint ? 'bg-gray-50 dark:bg-gray-900/40' : 'bg-white dark:bg-gray-900/70'}`}>
      <div className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">{label}</div>
      <div>
        <div className={`mt-1 font-serif tabular-nums text-gray-900 dark:text-gray-100 ${lead ? 'text-[28px] leading-none' : 'text-2xl'}`}
          style={lead ? { color: GOLD } : undefined}>{value}</div>
        {meter != null && <Meter value={meter} tone={tone} />}
        {sub ? <div className="mt-1.5 text-[11px] text-gray-400">{sub}</div> : null}
      </div>
    </div>
  );
}

function CompanyChip({ c }) {
  const color = STANCE[c.stance] || STANCE.neutral;
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium"
      style={{ background: `${color}1a`, color }}>
      {c.ticker || c.name}
    </span>
  );
}

function TakeawayCard({ r }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-200 p-4 pl-5 transition-colors hover:border-brand-gold/50 dark:border-gray-800">
      <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: GOLD, opacity: 0.55 }} />
      <div className="flex items-center gap-2 text-[11px] text-gray-400">
        <span className="truncate">{r.source_ref}</span>
        {r.published_at && <span className="shrink-0">· {new Date(r.published_at).toLocaleDateString()}</span>}
      </div>
      {r.thesis && (
        <p className="mt-1.5 font-serif text-[14px] leading-relaxed text-gray-800 dark:text-gray-100">{r.thesis}</p>
      )}
      {(r.topics?.length > 0 || r.companies?.length > 0) && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1">
          {r.companies?.map((c, i) => <CompanyChip key={`c${i}`} c={c} />)}
          {r.topics?.map((t, i) => (
            <span key={`t${i}`} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800 dark:text-gray-400">{t.title}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ChatterDashboard() {
  const [ov, setOv] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => researchosService.getResearchOverview()
      .then((r) => { if (alive) setOv(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 20000);   // refresh while a bulk run populates the graph
    return () => { alive = false; clearInterval(t); };
  }, []);

  const extractPct = pct(ov?.extracted, ov?.transcripts);
  const coverPct = pct(ov?.companies_covered, ov?.companies);

  const recent = ov?.recent || [];
  const shown = showAll ? recent : recent.slice(0, 2);

  const topMax = useMemo(
    () => Math.max(1, ...(ov?.top_companies || []).map((c) => c.mentions || 0)),
    [ov],
  );

  return (
    <div className="mb-6 flex flex-col gap-7">
      {/* ── header ── */}
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">YouTube Chatter</h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Podcasts &amp; interviews — transcript ingest, claim extraction, and a deterministic view of what the street is discussing.
          </p>
        </div>
        <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-green-500">● Live</span>
      </div>

      {/* ── statistics: ingest funnel + knowledge graph, one cohesive band ── */}
      <div>
        <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em]" style={{ color: GOLD }}>
          Pipeline
        </div>
        <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800">
          {/* gap-px over a tinted container renders clean 1px grid lines in BOTH axes, so the
              band stays crisp whether it lays out 4-wide (desktop) or wraps 2-wide (mobile). */}
          <div className="grid grid-cols-2 gap-px bg-gray-200 sm:grid-cols-4 dark:bg-gray-800">
            {/* ingest funnel */}
            <Cell lead label="Channels" value={nfmt(ov?.channels)}
              sub={ov?.x_handles ? `+ ${nfmt(ov.x_handles)} X handle${ov.x_handles === 1 ? '' : 's'}` : 'sources tracked'} />
            <Cell label="Transcripts" value={nfmt(ov?.transcripts)}
              sub={ov ? `${ov.x_docs ? `${nfmt(ov.x_docs)} X days · ` : ''}${nfmt(ov.pending)} awaiting extraction` : ''} />
            <Cell label="Extracted" value={nfmt(ov?.extracted)} meter={extractPct}
              sub={ov ? `${extractPct}% of transcripts` : ''} />
            <Cell label="Claims" value={nfmt(ov?.claims)} sub="extracted statements" />
            {/* knowledge graph (tinted) */}
            <Cell tint label="Companies" value={nfmt(ov?.companies)} meter={coverPct}
              sub={ov ? `${nfmt(ov.companies_covered)} US-listed` : ''} />
            <Cell tint label="Relationships" value={nfmt(ov?.relationships)} sub="graph edges" />
            <Cell tint label="Mentions" value={nfmt(ov?.observations)} sub="company observations" />
            <Cell tint label="Coverage" value={ov ? `${coverPct}%` : '—'} meter={coverPct}
              tone={coverPct >= 50 ? '#16a34a' : GOLD} sub="US-listed names" />
          </div>
        </div>
      </div>

      {/* ── latest takeaways (top 2, expandable) ── */}
      {recent.length > 0 && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[10px] font-medium uppercase tracking-[0.15em]" style={{ color: GOLD }}>
              Latest takeaways
            </div>
            <span className="text-[11px] tabular-nums text-gray-400">{recent.length} recent</span>
          </div>
          <div className="flex flex-col gap-3">
            {shown.map((r) => <TakeawayCard key={r.doc_id} r={r} />)}
          </div>
          {recent.length > 2 && (
            <button onClick={() => setShowAll((s) => !s)}
              className="mt-3 inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-medium text-gray-600 transition-colors hover:border-brand-gold hover:text-brand-gold dark:border-gray-800 dark:text-gray-300">
              {showAll ? '▲ Show less' : `▼ Show ${recent.length - 2} more`}
            </button>
          )}
        </div>
      )}

      {/* ── most discussed — ranked mention bars (a lead-in to the volume tracker) ── */}
      {ov?.top_companies?.length > 0 && (
        <div>
          <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em]" style={{ color: GOLD }}>
            Most discussed
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {ov.top_companies.slice(0, 12).map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-16 shrink-0 truncate text-xs font-medium text-gray-900 dark:text-gray-100">
                  {c.ticker || c.name}
                </span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                  <div className="h-full rounded-full"
                    style={{ width: `${pct(c.mentions, topMax)}%`, background: GOLD, opacity: 0.75 }} />
                </div>
                <span className="w-8 shrink-0 text-right text-[11px] tabular-nums text-gray-400">{c.mentions}</span>
                {c.coverage === 'not_covered' && (
                  <span className="shrink-0 text-[9px] uppercase tracking-wider text-amber-500">uncov</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
